import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Col, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { MdDeleteOutline, MdEdit } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import CustomModal from '../../components/CustomModal';
import Input from '../../components/input';
import MultiLineText from '../../components/MultiLineText';
import PaginationCustom from '../../components/Pagination';
import Select from '../../components/Select';
import { createServiceLineItem, deleteServiceLineItem, fetchCategoriesDropdown, fetchPartsList, fetchReasonsDropdown, fetchServicesReminder, fetchVehicleDetail, updateServiceLineItem } from '../../store/Slices/ServiceSlice';
import { dateMonthYear } from '../../utils/helper';
import { serviceLineSchema } from '../../utils/Schema';
import { lastCompleteIcon, nextDueIcon, saveLineItemIcon, searchIcon, serviceLineItemIcon } from '../../utils/svgs';
import { errorNotify, successNotify } from '../../utils/toast';

const ServiceLineItem = ({ vehicleDetailLoading, vehicleDetail, selectedVehicle, refreshVehicleDetail }) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [showAddItemBtn, setShowAddItemBtn] = useState(false);
    const [selectServiceItem, setSelectServiceItem] = useState(null);
    const [showServiceReminder, setShowServiceReminder] = useState(false)
    const [currentPage, setCurrentPage] = useState(1);
    const [editLineItem, setEditLineItem] = useState(null);

    const { categoriesLoading, categoriesData, reasonsLoading, reasonsData, partsLoading, partsData, createLineItemLoading, getServicesReminderLoading, getServicesReminderPaginated } = useSelector((state) => state.services);

    useEffect(() => {
        if (vehicleDetail) {
            dispatch(fetchReasonsDropdown());
            dispatch(fetchCategoriesDropdown());
            dispatch(fetchPartsList({ vehicleTypeId: vehicleDetail?.vehicle_type_id, vehicleId: vehicleDetail?.id }));
        }
    }, [vehicleDetail]);

    useEffect(() => {
        if (vehicleDetail) {
            dispatch(fetchServicesReminder({
                page: currentPage,
                limit: 5,
                vehicle_id: vehicleDetail?.id
            }));
        }
    }, [currentPage, vehicleDetail]);

    const serviceLineItemsOptions = partsData?.map((part) => ({
        value: part,
        product: part.part_number,
        description: part.description || '',
        brand: part.price,
        priceSource: part.vender?.name || '',
        manufacturer: part?.manufacturer?.name || '',
        label: part?.description
    })) || []

    const categoriesOptions = categoriesData?.map((category) => ({
        value: category.value,
        label: category.title
    })) || []

    const reasonsOptions = reasonsData?.map((reason) => ({
        value: reason.value,
        label: reason.title
    })) || []

    const initialValues = {
        vehicle_id: vehicleDetail?.id || '',
        part_id: selectServiceItem?.id || '',
        service_issue_id: 0,
        service_category_id: selectServiceItem?.maintenance_category_id || '',
        cost_parts: selectServiceItem?.price || '',
        rate: selectServiceItem?.price || '',
        qty: '',
        cost_labor: '',
        sub_total: '',
        odo_meter: vehicleDetail?.odm || '',
        repair_reason_id: '',
        description: ''
    };

    const calculateSubTotalWithQty = (rate, qty, labor) => {
        const rateValue = parseFloat(rate) || 0;
        const qtyValue = parseInt(qty) || 0;
        const laborValue = parseFloat(labor) || 0;
        return ((rateValue * qtyValue) + laborValue).toFixed(2);
    }

    const handleServiceLineSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const lineItemData = {
                vehicle_id: parseInt(values.vehicle_id),
                part_id: parseInt(values.part_id),
                service_id: id ? parseInt(id) : parseInt(vehicleDetail?.max_id) + 1 || 1,
                service_issue_id: parseInt(values.service_issue_id),
                service_category_id: parseInt(values.service_category_id),
                cost_parts: parseFloat(values.cost_parts),
                rate: parseFloat(values.rate),
                qty: parseInt(values.qty),
                cost_labor: parseFloat(values.cost_labor),
                sub_total: parseFloat(values.sub_total),
                odo_meter: parseInt(values.odo_meter),
                repair_reason_id: parseInt(values.repair_reason_id),
                description: values.description
            };

            if (editLineItem) {
                await dispatch(updateServiceLineItem({ service_line_item_id: editLineItem.id, ...lineItemData })).unwrap();
                successNotify('Service line item updated successfully');
            } else {
                await dispatch(createServiceLineItem(lineItemData)).unwrap();
                successNotify('Service line item added successfully');
            }
            resetForm();
            setSelectServiceItem(null);
            setShowAddItemBtn(false);
            setEditLineItem(null);

            // Use shared refresh function instead of direct API call
            if (refreshVehicleDetail) {
                await refreshVehicleDetail();
            }
        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleServiceItemSelect = (selectedOption) => {
        editLineItem && setEditLineItem(null);
        setSelectServiceItem(selectedOption?.value || null);
    };

    const serviceLineReminder = <CustomModal heading="Service Reminders" size="lg" show={showServiceReminder} handleClose={() => setShowServiceReminder(false)}>
        <div className='service_line_items_main' style={{ border: "none", padding: "0px" }}>
            {
                getServicesReminderLoading ? <div className='no_vehicle_selected'>
                    <p>Loading...</p>
                </div> :
                    <div className='service_line_items_table' style={{ borderRadius: "0px" }}>
                        {
                            getServicesReminderPaginated?.results?.length === 0 ?
                                <div className='no_vehicle_selected'>
                                    <p>No service reminders found.</p>
                                </div> :
                                <>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Description</th>
                                                <th>Service Schedule</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                getServicesReminderPaginated?.results?.map((item, index) => {
                                                    const serviceItems = item?.service_line_items || [];

                                                    const firstItem = serviceItems[0]?.reason?.title || 'N/A';
                                                    const remainingCount = serviceItems.length - 1;

                                                    return (
                                                        <tr key={index + 1}>
                                                            <td>
                                                                <div className="service-tasks-container" style={{ position: 'relative' }}>
                                                                    <span className="first-task">{firstItem}</span>
                                                                    {
                                                                        remainingCount > 0 && (
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip>
                                                                                        <div>
                                                                                            {serviceItems.slice(1).map((item, idx) => (
                                                                                                <div key={idx} className='tooltip_item'>{item?.reason?.title}</div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="additional-tasks">+{remainingCount}</span>
                                                                            </OverlayTrigger>

                                                                        )
                                                                    }
                                                                </div>
                                                            </td>
                                                            <td>{item?.complete_at ? dateMonthYear(item?.complete_at) : ''}</td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </Table>
                                    <PaginationCustom
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                        totalItems={getServicesReminderPaginated?.meta?.totalItems}
                                        totalPages={getServicesReminderPaginated?.meta?.totalPages}
                                        showLimit={false}
                                        className="service_line_items_pagination"
                                    />
                                </>
                        }

                    </div>
            }
        </div>
    </CustomModal>

    const handleDeleteServiceLineItem = async (id) => {
        try {
            await dispatch(deleteServiceLineItem(id)).unwrap();
            successNotify('Service Line Item deleted successfully');
            // Use shared refresh function instead of callApi
            if (refreshVehicleDetail) {
                await refreshVehicleDetail();
            }
        } catch (error) {
            errorNotify(error);
        }
    }

    const handleEditLineItem = (item) => {
        console.log(item)
        setEditLineItem(item);
        setSelectServiceItem(item);
        setShowAddItemBtn(true);
    };

    console.log(editLineItem)

    return (
        <div className='service_line_items_main'>
            {serviceLineReminder}
            <div className='d-flex justify-content-between align-items-center'>
                <h6>Service Line Items</h6>

                <button disabled={!vehicleDetail} type='button' className='reminder_service_btn' onClick={() => setShowServiceReminder(true)}>{serviceLineItemIcon} View Service Reminders</button>
            </div>
            {
                !vehicleDetail ?
                    <div className='no_vehicle_selected'>
                        {
                            vehicleDetailLoading ? <p>Loading...</p> :
                                <p>No vehicle selected. Please select a vehicle to track issues.</p>
                        }
                    </div> :
                    <>
                        <div className='service_line_items_table'>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Description</th>
                                        <th>Parts (PKR)</th>
                                        <th>Labor (PKR)</th>
                                        <th>Subtotal (PKR)</th>
                                        <th>Notes</th>
                                        <th>Categorizations</th>
                                        <th>Linked Issues</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        vehicleDetail?.vehicle_service_line_items ? vehicleDetail?.vehicle_service_line_items?.map((item, index) => {
                                            return (
                                                <tr key={index + 1}>
                                                    <td>{item?.part ? item?.part?.description : 'Other'}</td>
                                                    <td>{item?.cost_parts}</td>
                                                    <td>{item?.cost_labor}</td>
                                                    <td>{item?.sub_total}</td>
                                                    <td>
                                                        <div className="two-line-ellipsis">
                                                            <MultiLineText text={item?.description} maxWidth={"70px"} />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="two-line-ellipsis">
                                                            <MultiLineText span={true} text={item?.category?.title} maxWidth={"110px"} />
                                                        </div>
                                                    </td>
                                                    <td><a>Link</a></td>
                                                    <td>
                                                        <div>
                                                            <MdEdit className='edit_line_item_icon' onClick={() => handleEditLineItem(item)} />
                                                            <MdDeleteOutline className='delete_line_item_icon' onClick={() => handleDeleteServiceLineItem(item?.id)} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }) :
                                            vehicleDetail?.service_line_items?.map((item, index) => {
                                                return (
                                                    <tr key={index + 1}>
                                                        <td>{item?.part ? item?.part?.description : 'Other'}</td>
                                                        <td>{item?.cost_parts}</td>
                                                        <td>{item?.cost_labor}</td>
                                                        <td>{item?.sub_total}</td>
                                                        <td>
                                                            <div className="two-line-ellipsis">
                                                                <MultiLineText text={item?.description} maxWidth={"70px"} />
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="two-line-ellipsis">
                                                                <MultiLineText span={true} text={item?.category?.title} maxWidth={"110px"} />
                                                            </div>
                                                        </td>
                                                        <td><a>Link</a></td>
                                                        <td>
                                                            <div>
                                                                <MdEdit className='edit_line_item_icon' onClick={() => handleEditLineItem(item)} />
                                                                <MdDeleteOutline className='delete_line_item_icon' onClick={() => handleDeleteServiceLineItem(item?.id)} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                    }
                                </tbody>
                            </Table>
                            {
                                showAddItemBtn &&
                                <>
                                    {
                                        selectServiceItem &&
                                        <Formik
                                            initialValues={editLineItem ? {
                                                vehicle_id: vehicleDetail?.id || '',
                                                part_id: editLineItem?.part_id || '',
                                                service_issue_id: editLineItem?.service_issue_id || 0,
                                                service_category_id: editLineItem?.service_category_id || '',
                                                cost_parts: editLineItem?.cost_parts || '',
                                                rate: editLineItem?.rate || '',
                                                qty: editLineItem?.qty || '',
                                                cost_labor: editLineItem?.cost_labor || '',
                                                sub_total: editLineItem?.sub_total || '',
                                                odo_meter: editLineItem?.odo_meter || vehicleDetail?.odm || '',
                                                repair_reason_id: editLineItem?.repair_reason_id || '',
                                                description: editLineItem?.description || ''
                                            } : initialValues}
                                            validationSchema={serviceLineSchema}
                                            onSubmit={handleServiceLineSubmit}
                                            enableReinitialize={true}
                                        >
                                            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm }) => (
                                                <div className='service_line_form'>
                                                    <div>
                                                        <div className='service_line_item_header'>
                                                            <h5 style={{ maxWidth: "120px" }}>{editLineItem ? editLineItem?.part?.description || 'Other' : selectServiceItem?.description || 'Other'}</h5>

                                                            <div className='d-flex gap-2'>
                                                                {/* <Input
                                                                    label="Parts (PKR)"
                                                                    type="hidden"
                                                                    name="cost_parts"
                                                                    value={values.cost_parts}
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        setFieldValue('sub_total', calculateSubTotal(e.target.value, values.cost_labor));
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    className='service_entry_price'
                                                                    error={errors.cost_parts}
                                                                    touched={touched.cost_parts}

                                                                /> */}
                                                                <Input
                                                                    label="Unit Price (PKR)"
                                                                    type="number"
                                                                    name="rate"
                                                                    value={values.rate}
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        const newRate = parseFloat(e.target.value) || 0;
                                                                        const qty = parseInt(values.qty) || 0;
                                                                        const costParts = newRate * qty;
                                                                        // setFieldValue('cost_parts', costParts);
                                                                        setFieldValue('sub_total', calculateSubTotalWithQty(e.target.value, values.qty, values.cost_labor));
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    className='service_entry_price'
                                                                    error={errors.rate}
                                                                    touched={touched.rate}
                                                                />
                                                                <Input
                                                                    label="Quantity"
                                                                    type="number"
                                                                    name="qty"
                                                                    value={values.qty}
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        const newQty = parseInt(e.target.value) || 0;
                                                                        const rate = parseFloat(values.rate) || 0;
                                                                        const costParts = rate * newQty;
                                                                        setFieldValue('cost_parts', costParts);
                                                                        setFieldValue('sub_total', calculateSubTotalWithQty(values.rate, e.target.value, values.cost_labor));
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    className='service_entry_price'
                                                                    error={errors.qty}
                                                                    touched={touched.qty}
                                                                />
                                                                <Input
                                                                    label="Labor (PKR)"
                                                                    type="number"
                                                                    name="cost_labor"
                                                                    value={values.cost_labor}
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        setFieldValue('sub_total', calculateSubTotalWithQty(values.rate, values.qty, e.target.value));
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    className='service_entry_price'
                                                                    error={errors.cost_labor}
                                                                    touched={touched.cost_labor}
                                                                />
                                                                <Input
                                                                    label="Subtotal (PKR)"
                                                                    type="number"
                                                                    name="sub_total"
                                                                    value={values.sub_total}
                                                                    className='service_entry_price'
                                                                    error={errors.sub_total}
                                                                    touched={touched.sub_total}
                                                                />
                                                            </div>

                                                            <div className='save_button_container d-flex align-items-center' style={{ paddingRight: "3px", gap: "2px" }}>
                                                                <button
                                                                    className="save_line_item_btn"
                                                                    type="button"
                                                                    disabled={createLineItemLoading}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        submitForm();
                                                                    }}>
                                                                    {saveLineItemIcon}
                                                                </button>
                                                                <MdDeleteOutline style={{ fontSize: "22px" }} className='delete_line_item_icon' onClick={() => { setSelectServiceItem(null); setEditLineItem(null); }} />
                                                            </div>
                                                        </div>
                                                        <div className='service_line_item_details'>
                                                            <p>{lastCompleteIcon} <span>Last Completed:</span> {selectServiceItem?.part_service_line_item ? dateMonthYear(selectServiceItem?.part_service_line_item?.service_short_info?.complete_at) : '-'} . {selectServiceItem?.part_service_line_item ? selectServiceItem?.part_service_line_item?.odo_meter : '-'} km ago</p>
                                                            <p>{nextDueIcon} <span>Next Due:</span> 3 months from now</p>
                                                        </div>

                                                        <textarea
                                                            rows={3}
                                                            placeholder='Add notes or additional details'
                                                            name="description"
                                                            value={values.description}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.description && errors.description && (
                                                            <div className="error-message" style={{ color: '#ff0000de', fontSize: '12px', marginTop: "3px" }}>
                                                                {errors.description}
                                                            </div>
                                                        )}

                                                        <div className='service_line_item_categorization'>
                                                            <h6>Maintenance Categorization</h6>

                                                            <div>
                                                                <Row>
                                                                    <Col md={4}>
                                                                        <Select
                                                                            label="Category"
                                                                            placeholder="Select Category"
                                                                            options={categoriesOptions}
                                                                            loading={categoriesLoading}
                                                                            name="service_category_id"
                                                                            value={categoriesOptions.find(option => option.value === values.service_category_id) || null}
                                                                            onChange={(selectedOption) => setFieldValue('service_category_id', selectedOption?.value || '')}
                                                                            onBlur={handleBlur}
                                                                            error={errors.service_category_id}
                                                                            touched={touched.service_category_id}
                                                                            isSearch={true}
                                                                        />
                                                                    </Col>
                                                                    <Col md={4}>
                                                                        <Select
                                                                            label="Reason for Repair"
                                                                            placeholder="Select Reason for Repair"
                                                                            options={reasonsOptions}
                                                                            loading={reasonsLoading}
                                                                            name="repair_reason_id"
                                                                            value={reasonsOptions.find(option => option.value === values.repair_reason_id) || null}
                                                                            onChange={(selectedOption) => setFieldValue('repair_reason_id', selectedOption?.value || '')}
                                                                            onBlur={handleBlur}
                                                                            error={errors.repair_reason_id}
                                                                            touched={touched.repair_reason_id}
                                                                            isSearch={true}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Formik>
                                    }
                                    {
                                        !editLineItem &&
                                        <div className='service_line_items_search'>
                                            <Select
                                                label=""
                                                placeholder="Search and Select Item"
                                                options={serviceLineItemsOptions}
                                                loading={partsLoading}
                                                name=""
                                                value={serviceLineItemsOptions.find(option => option.value?.id === selectServiceItem?.id) || null}
                                                onChange={handleServiceItemSelect}
                                                showIcon={true}
                                                icon={searchIcon}
                                                isSearch={true}
                                            />
                                        </div>
                                    }
                                </>
                            }
                        </div>
                        <div>
                            {
                                !editLineItem &&
                                <button
                                    disabled={!vehicleDetail}
                                    className='add_issue_btn'
                                    type="button"
                                    onClick={() => setShowAddItemBtn(!showAddItemBtn)}>+ Add Line Item</button>
                            }
                        </div>
                    </>
            }
        </div>
    )
}

export default ServiceLineItem